'use strict';

const genericToast = require('./genericToast');

/**
 * Localized fallback for Gigya auth failures (network / missing server error).
 * @returns {string}
 */
function getAuthErrorMessage() {
	return (
		$('#gigya-handle').attr('data-auth-error') ||
		$('.page').attr('data-gigya-auth-error') ||
		''
	);
}

/**
 * Shows an auth error via the site standard toast.
 * @param {string} [message]
 */
function showAuthError(message) {
	const text = String(message || getAuthErrorMessage() || '').trim();
	if (!text) {
		return;
	}

	genericToast.show({
		message: text,
		iconClass: 'icon-exclamation-triangle',
		error: true
	});
}

function handleAuthResponse(ajaxResponse, eventObj, options, eventType, profilePayload, isBusiness) {
	if (ajaxResponse.success && window.lavazzaGtm?.auth) {
		window.lavazzaGtm.auth.trackLoginOk(eventObj, {
			eventType: eventType,
			profile: profilePayload,
			isBusiness: isBusiness,
			digitalId: ajaxResponse.digitalId,
			customer_email: ajaxResponse.customer_email,
			customer_type: ajaxResponse.customer_type,
			customer_gender: ajaxResponse.customer_gender,
			customer_birth: ajaxResponse.customer_birth,
			privacy_profilazione: ajaxResponse.privacy_profilazione,
			privacy_invio_promo: ajaxResponse.privacy_invio_promo,
			privacy_informativa: ajaxResponse.privacy_informativa,
			customer_sub_type: ajaxResponse.customer_sub_type,
			registered_machine_code: ajaxResponse.registered_machine_code,
			registered_machine_name: ajaxResponse.registered_machine_name
		});
	}

	if (ajaxResponse.success && ajaxResponse.redirectUrl) {
		if (!options.skipRedirect) {
			window.location.href = ajaxResponse.redirectUrl;
		}
		return;
	}

	// On behalf: the Gigya session set by the server fires onLogin, the rejection is expected
	if (!ajaxResponse.success && !ajaxResponse.onBehalf) {
		showAuthError(ajaxResponse.error);
	}
}

/**
 * Social registration onAfterSubmit has no provider: fall back to the social button clicked
 * (stored by gtm.js initSocialProviderCapture).
 * @param {Object} eventObj
 * @returns {string}
 */
function resolveLoginProvider(eventObj) {
	let provider = (eventObj && (eventObj.provider || eventObj.loginProvider || (eventObj.response && eventObj.response.loginProvider))) || '';

	if (provider) {
		return provider;
	}

	try {
		return window.sessionStorage.getItem('lvzTealiumSocialProvider') || '';
	} catch (e) {
		return '';
	}
}

function submitAuth(postUrl, eventType, uid, signature, timestamp, profilePayload, isBusiness, eventObj, options) {
	$.ajax({
		url: postUrl,
		type: 'POST',
		dataType: 'json',
		data: {
			type: eventType,
			uid: uid,
			signature: signature,
			timestamp: timestamp,
			profile: JSON.stringify(profilePayload),
			isBusiness: isBusiness,
			// Gigya onLogin "provider" (site, google, facebook, apple, amazon...) for Tealium registration_type
			loginProvider: resolveLoginProvider(eventObj)
		},
		success: function (ajaxResponse) {
			handleAuthResponse(ajaxResponse, eventObj, options, eventType, profilePayload, isBusiness);
		},
		error: function () {
			showAuthError();
		}
	});
}

function getAuthData(eventObj, options) {
	const response = eventObj.response || eventObj || {};
	const profile = response.profile || eventObj.profile || eventObj.user || {};
	const screenId = String(
		eventObj.currentScreen || eventObj.screen || eventObj.prevScreen || options.activeScreen || ''
	).toLowerCase();

	return {
		postUrl: options.postUrl,
		profile: profile,
		uid: response.UID || eventObj.UID,
		signature: response.UIDSignature || eventObj.UIDSignature,
		timestamp: response.signatureTimestamp || eventObj.signatureTimestamp,
		eventType: options.eventType || response.operation || eventObj.eventName,
		isBusiness: screenId.includes('register-company')
	};
}

function hasRequiredAuthData(authData, options) {
	if (!authData.postUrl || !authData.uid || !authData.signature || !authData.timestamp) {
		return false;
	}

	return !(options.requireEventType && !authData.eventType);
}

function getEventType(eventType) {
	return eventType || 'login';
}

function submitWithAccountProfile(authData, eventObj, options) {
	window.gigya.accounts.getAccountInfo({
		include: 'profile,data',
		callback: function (accountInfo) {
			const profile = accountInfo?.errorCode === 0 && accountInfo.profile
				? accountInfo.profile
				: authData.profile;

			submitAuth(
				authData.postUrl,
				authData.eventType,
				authData.uid,
				authData.signature,
				authData.timestamp,
				profile,
				authData.isBusiness,
				eventObj,
				options
			);
		}
	});
}

/**
 * POSTs Gigya session data to GigyaHandle-gigyaAuth to sync with SFCC.
 * @param {Object} eventObj - Gigya onLogin / onAfterSubmit / getAccountInfo payload.
 * @param {Object} options
 * @param {string} options.postUrl - GigyaHandle-gigyaAuth endpoint URL.
 * @param {string} [options.eventType] - Force event type (defaults to Gigya operation/eventName or 'login').
 * @param {boolean} [options.requireEventType=false] - Skip the request when no event type is available.
 * @param {boolean} [options.skipRedirect=false] - Sync the session without following redirectUrl.
 * @returns {boolean} Whether the request was sent.
 */
function postGigyaAuth(eventObj, options = {}) {
	const authData = getAuthData(eventObj, options);

	if (!hasRequiredAuthData(authData, options)) {
		return false;
	}

	authData.eventType = getEventType(authData.eventType);

	// onLogin profile is often partial (no birthDay/birthMonth/birthYear); load full profile first.
	if (window.gigya?.accounts) {
		submitWithAccountProfile(authData, eventObj, options);
		return true;
	}

	submitAuth(
		authData.postUrl,
		authData.eventType,
		authData.uid,
		authData.signature,
		authData.timestamp,
		authData.profile,
		authData.isBusiness,
		eventObj,
		options
	);
	return true;
}

module.exports = {
	postGigyaAuth: postGigyaAuth
};
